import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    Card, 
    CardHeader, 
    Avatar,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Paper,
    PaperProps,
    Box
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDataProvider, useRefresh, useNotify } from "react-admin";
import ApiResources from "src/providers/ApiResources";
import Draggable from 'react-draggable';
import BusinessIcon from "@mui/icons-material/Business";
import HomeIcon from "@mui/icons-material/Home";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import moment from "moment";

interface TrackingModalProps {
    shipment: any;
    open: boolean;
    setOpen: any;
    title: string;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const TrackingModal = (props: TrackingModalProps) => {
    const { shipment, open, setOpen, title } = props;

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();

    useEffect(() => {
        if (shipment !== null) {
            setData(null);
            getTracking();
        }
    }, []);

    const getTracking = async () => {
        setLoading(true);
        dataProvider
        .get(ApiResources.getCourierTracking(false), {
            courier: shipment?.courier,
            tracking_number: shipment?.awb
        })
        .then((res) => {
            setLoading(false);
            setData(res.data);
        })
        .catch((error) => {
            setLoading(false);
            setOpen(false);
            notify(error.message, { type: 'warning' });
        });
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="md"
        >
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    { loading &&
                        <Grid item sm={12} md={12} justifyContent="center" display="flex">
                            <CircularProgress size={32} thickness={3} sx={{color: "#000", margin: "20px"}}/>
                        </Grid>
                    }

                    { data && (
                        data.current.status == "Delivered" ?
                            <Grid item sm={12} md={12}>
                                <TrackingCard 
                                    data={{...data.destination, ...data.current}}
                                    title="Destination"
                                    icon={<HomeIcon color="primary" sx={{ fontSize: "16px", color: "#14B8A6" }} />}
                                    highlight
                                />
                            </Grid>  
                        :
                            <>
                            <Grid item sm={12} md={4}>
                                <TrackingCard 
                                    data={data.origin}
                                    title="Origin"
                                    icon={<BusinessIcon sx={{ fontSize: "16px", color: "#14B8A6" }} />}
                                    highlight
                                />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <TrackingCard
                                    data={data.current} 
                                    title={data.current.status}
                                    icon={<LocalShippingIcon sx={{ fontSize: "16px", color: "#14B8A6" }} />}
                                    highlight
                                />
                            </Grid>
                            <Grid item sm={12} md={4}>
                                <TrackingCard 
                                    data={data.destination}
                                    title="Destination"
                                    icon={<HomeIcon color="primary" sx={{ fontSize: "16px" }} />}
                                />
                            </Grid>                      
                            </>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

const TrackingCard = (props) => {
    const { data, title, icon, highlight = false } = props;

    const cardStyle = {
        margin: "10px 10px 10px 0" ,
        padding: "5px",
        "&.highlight": {
            border: '2px solid #14B8A6',
            backgroundColor: '#f8fffe'
        },
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.1), 0 6px 20px 0 rgba(0, 0, 0, 0.05)",
        border: "2px solid #ffffff",
        minHeight: "180px"
    };
    
    const sectionStyle = {
        margin: "15px",
    };
    
    const textStyle = { fontSize: "12px", display: 'block' };

    return (
        <Card
            sx={cardStyle}
            className={highlight ? 'highlight' : ''}
        >
            <CardHeader
                sx={{ padding: "12px" }}
                avatar={
                    <Avatar
                        sx={{
                        bgcolor: "#f2f2f2",
                        }}
                    >
                        {icon}
                    </Avatar>
                }
                title={title}
                titleTypographyProps={{ fontSize: "14px", fontWeight: "500" }}
                subheader={data.description ?? null}
            />

            <Box sx={sectionStyle}>
                <Typography
                    sx={textStyle}
                    style={{fontWeight: 500, marginBottom: '10px'}}
                >
                    {data.date? moment(data.date).format('MMMM Do YYYY, h:mm a') : null}
                </Typography>

                <Typography
                    sx={textStyle}
                    style={{fontWeight: 500, marginBottom: '10px'}}
                >
                    {data.reference}
                </Typography>

                <Typography
                    sx={textStyle}
                >
                    {data.address?.city} {data.address?.postalCode}
                </Typography>
                <Typography
                    sx={textStyle}
                >
                    {data.address?.stateOrProvinceCode} {data.address?.countryName}
                </Typography>
            </Box>
        </Card>
    )
};